/**
 * Expanding Box module
 * @module 'modules/expanding-box'
 *
 * Allows an element to be closed and opened with an animation
 */

/* IE 9+ */
if (!Element.prototype.matches) {
  Element.prototype.matches = Element.prototype.msMatchesSelector;
}

import delegateListener from '../modules/delegate-listener';

class ExpandingBox {
  /**
   * Construct an ExpandingBox instance with options. This supports multiple elements with a caveat; the order of each selected element should correspond with the other elements that comprise a single expanding box.
   * The index of selected elements should correspond with the intended root-opener-expander group
   *
   * @param {object} [options] - Initialization options
   * @param {string} [options.rootSelector] - A selector matching the intended root elements
   * @param {string} [options.openerSelector] - A selector matching the elements that should open the expanders
   * @param {string} [options.expanderSelector] - A selector matching the intended expander elements
   * @param {string} [options.closedClass] - A class name that indicates a closed expander. It will be applied on the root element
   * @param {number} [options.animationDuration] - The duration of the opening/closing animation (in milliseconds)
   * @param {(boolean|string)} [options.initiallyClosed] - Setting this to `true` will instantly close all expanding boxes on initialization. Alternatively a selector string may be used to filter elements that should initially be closed
   */
  constructor(options) {
    const defaults = {
      rootSelector: '.js-expanding__container',
      openerSelector: '.js-expanding__opener',
      expanderSelector: '.js-expanding__expander',
      closedClass: 'js-expanding--closed',
      closingClass: 'js-expanding--closing',
      openClass: 'js-expanding--open',
      openingClass: 'js-expanding--opening',
      animationDuration: 300,
      initiallyClosed: ':not(.js-expanding--initially-open)',
      closedLabelDataset: 'labelOpen',
      openLabelDataset: 'labelClosed',
    }
    this.delegateListener = delegateListener;

    this.settings = Object.assign({}, defaults, options);
    this.s = this.settings;
  }

  /**
   * Initializes the Expanding Boxes, by populating nodeLists and binding events for the elements within the nodeLists. Optionally closes each Expanding Box, depending on the ExpandingBox instance's settings
   */
  init() {
    try {
      // Usage (in browser console): localStorage.setItem('svt-expanding-box-scroll-behavior', 'none');
      // Reset with: localStorage.removeItem('svt-expanding-box-scroll-behavior');
      this._scrollBehaviorOverride = localStorage.getItem('svt-expanding-box-scroll-behavior') || false;
    } catch(e) { void ''; }

    this._bindEvents();

    if(this.s.initiallyClosed) {
      if(typeof this.s.initiallyClosed === 'string') {
        this._closeFiltered(this.s.initiallyClosed);
      } else {
        this._closeAll();
      }
    }
  }

  /**
   * Magic getters for up-to-date element collections
   */
  get _rootElements () {
    return Array.from(document.querySelectorAll(this.s.rootSelector));
  }
  get _openerElements () {
    return Array.from(document.querySelectorAll(this.s.openerSelector));
  }
  get _expanderElements () {
    return Array.from(document.querySelectorAll(this.s.expanderSelector));
  }

  /**
   * Binds events for opener elements
   */
  _bindEvents() {
    this._boundOpenerClickHandler = this._handleOpenerClick.bind(this);
    document.body.addEventListener('click', this.delegateListener(
      this.s.openerSelector, this._boundOpenerClickHandler
    ));

    this._boundStageCompletehandler = this._handleStageComplete.bind(this);
    document.body.addEventListener('stage:complete', this.delegateListener(
      this.s.rootSelector, this._boundStageCompletehandler
    ));
  }

  /**
   * Instantly closes all expander elements that don't match the given selector
   *
   * @param {string} selector - The selector that we're matching to
   */
  _closeFiltered(selector) {
    this._rootElements.filter(
      el => el.matches(selector)
    ).forEach(this._addClosedClass, this);

    this._expanderElements.filter(
      (el, index) => this._rootElements[index].matches(selector)
    ).forEach(this._zeroHeight, this);
  }

  /**
   * Instantly closes all expander elements
   */
  _closeAll() {
    this._rootElements.forEach(this._addClosedClass, this);
    this._expanderElements.forEach(this._zeroHeight, this);
  }

  /**
   * Removes all state classes from a given element
   *
   * @param {HTMLElement} element - The element the classes are removed from
   */
  _clearStateClasses(element) {
    element.classList.remove(...[
      this.s.openClass, this.s.openingClass,
      this.s.closedClass, this.s.closingClass
    ]);
  }

  /**
   * Adds the `open` class to a given element
   *
   * @param {HTMLElement} element - The element the class is applied to
   */
  _addOpenClass(element) {
    this._clearStateClasses(element);
    element.classList.add(this.s.openClass);
  }

  /**
   * Adds the `opening` class to a given element
   *
   * @param {HTMLElement} element - The element the class is applied to
   */
  _addOpeningClass(element) {
    this._clearStateClasses(element);
    element.classList.add(this.s.openingClass);
  }

  /**
   * Adds the `closing` class to a given element
   *
   * @param {HTMLElement} element - The element the class is applied to
   */
  _addClosingClass(element) {
    this._clearStateClasses(element);
    element.classList.add(this.s.closingClass);
  }

  /**
   * Adds the `closed` class to a given element
   *
   * @param {HTMLElement} element - The element the class is applied to
   */
  _addClosedClass(element) {
    this._clearStateClasses(element);
    element.classList.add(this.s.closedClass);
  }

  /**
   * Sets an element's height to zero
   *
   * @param {HTMLElement} element - The element that's height should be zeroed
   */
  _zeroHeight(element) {
    element.style.height = '0px';
  }

  /**
   * Toggles the collapsed state of the corresponding expander box
   * @param {object} event - Event object, in which the opener element is the currentTarget
   */
  _handleOpenerClick(event) {
    let target = event.delegateTarget || event.target;
    let root = this._findCorresponding(
      target,
      this._openerElements,
      this._rootElements
    );
    let expander = this._findCorresponding(
      target,
      this._openerElements,
      this._expanderElements
    );
    let scrollBehavior = expander.dataset['scrollBehavior'];
    if(!root.classList.contains(this.s.closedClass)) {
      this._collapse(expander, scrollBehavior);
    } else {
      this._expand(expander);
    }
  }

  /**
   * Collapses the corresponding expander box.
   * Intended for use with custom events. See item-view.js
   *
   * @param {CustomEvent} event - The custom event. It's target or delegateTarget should be the expander root element.
   */
  _handleStageComplete(event) {
    let root = event.delegateTarget || event.target;
    let expander = this._findCorresponding(
      root,
      this._rootElements,
      this._expanderElements
    );
    let scrollBehavior = expander.dataset['scrollBehavior'];
    this._collapse(expander, scrollBehavior);
  }

  /**
   * Given a needle and a haystack, finds a corresponding element by it's index
   *
   * @param {HTMLElement} needle - The element that is being searched
   * @param {(array|NodeList)} haystack - The list of elements, to which the needle belongs to
   * @param {(array|NodeList)} correspondingHaystack - The list of corresponding elements
   *
   * @returns {(HTMLElement|null)} - Returns the corresponding element if found, otherwise null
   */
  _findCorresponding(needle, haystack, correspondingHaystack) {
    let index = haystack.indexOf(needle);
    return index < 0 ? null : correspondingHaystack[index];
  }

  /**
   * Collapses a given element by interpolating between it's current height and 0
   *
   * @param {HTMLElement} expander - The element that should collapse
   * @param {String} scrollBehavior - When/whether the view should be scrolled back to the opener [parallel/serial/none]
   */
  _collapse(expander, scrollBehavior) {
    if(this._scrollBehaviorOverride) {
      scrollBehavior = this._scrollBehaviorOverride;
    }
    let visibleHeight = window.innerHeight - expander.getBoundingClientRect().top;
    let elementHeight = parseInt(window.getComputedStyle(expander).height, 10);

    let currentHeight = Math.min(elementHeight, visibleHeight);

    let targetHeight = 0;
    let rootElement = this._findCorresponding(expander, this._expanderElements, this._rootElements);
    let openerElement = this._findCorresponding(expander, this._expanderElements, this._openerElements);
    let scrollToOptions = {
      left: 0,
      behavior: 'smooth'
    };
    this._addClosingClass(rootElement);
    if(scrollBehavior === 'parallel') {
      window.scrollTo(Object.assign(scrollToOptions,{
        top: openerElement.getBoundingClientRect().top + document.documentElement.scrollTop,
      }));
    }
    let step = this._animateHeight(expander, currentHeight, targetHeight, (function() {
      expander.style.height = targetHeight + 'px';
      this._addClosedClass(rootElement);
      this._setLabelState(openerElement, false);
      if(scrollBehavior === 'serial') {
        window.scrollTo(Object.assign(scrollToOptions,{
          top: openerElement.getBoundingClientRect().top + document.documentElement.scrollTop,
        }));
      }
    }).bind(this));
    requestAnimationFrame(step);
  }

  /**
   * Expands a given element by interpolating between it's current height and it's ideal (auto) height
   *
   * @param {HTMLElement} expander - The element that should expand
   */
  _expand(expander) {
    let currentHeight = parseInt(window.getComputedStyle(expander).height, 10);
    expander.style.height = 'auto';
    let elementHeight = parseInt(window.getComputedStyle(expander).height, 10);
    let visibleHeight = window.innerHeight - expander.getBoundingClientRect().top;
    let targetHeight = Math.min(elementHeight, visibleHeight);
    let rootElement = this._findCorresponding(
      expander, this._expanderElements, this._rootElements
    );
    expander.style.height = currentHeight + 'px';
    this._addOpeningClass(rootElement);
    let step = this._animateHeight(expander, currentHeight, targetHeight, (function() {
      expander.style.height = '';
      this._addOpenClass(rootElement);
      this._setLabelState(this._findCorresponding(
        expander, this._expanderElements, this._openerElements
      ), true);
    }).bind(this));
    requestAnimationFrame(step);
  }

  /**
  * Sets opener text according to element's data-attributes, if available.
  *
  * @param {HTMLElement} opener - The subject opener element
  * @param {boolean} state - Truthy for opened (=show less), falsey for closed (=show more)
  */
  _setLabelState(opener, state) {
    let text;
    if(state) {
      text = opener.dataset[this.s.openLabelDataset];
    } else {
      text = opener.dataset[this.s.closedLabelDataset];
    }
    if(text) {
      opener.innerText = text
    }
  }

  /**
   * Animates an element's height by interpolating between given starting and ending values
   *
   * @param {HTMLElement} subject - The element that should be animated
   * @param {number} startHeight - The initial height of the element
   * @param {number} endHeight - The target (final) height of the element
   * @param {function} [complete] - An optional callback function, that gets called when the animation is complete
   *
   * @returns {function} - A bound function that can be passed to requestAnimationFrame, setInterval, or such.
   * @param {number} time - A timestamp that is passed to the bound function on each call (requestAnimationFrame does this automatically).
   */
  _animateHeight(subject, startHeight, endHeight, complete) {
    let startTime = false;
    let step = function(t) {
      if(!startTime) {
        startTime = t;
      }
      let position = (t - startTime) / this.s.animationDuration;
      if(position <= 1) {
        subject.style.height = this._easeOutCubic(
          startHeight, endHeight, position
        ) + 'px';
        requestAnimationFrame(step.bind(this));
      } else {
        if(typeof complete === 'function') {
          complete();
        }
      }
    };
    return step.bind(this);
  }

  /**
   * Linear interpolation function
   * NOTE: does not clamp the values!
   *
   * @param {number} a - The starting value
   * @param {number} b - The ending value
   * @param {number} t - The interpolation point
   *
   * @returns {number} - The interpolated value
   */
  _lerp(a, b, t) {
    return (1 - t) * a + t * b;
  }

  /**
   * Cubic interpolation function, where the rate of change decreases as t gets closer to 1
   * @param {number} a - The starting value
   * @param {number} b - The ending value
   * @param {number} t - The interpolation point
   *
   * @returns {number} - The interpolated value
   */
  _easeOutCubic(a, b, t) {
    return this._lerp(a, b, (--t) * t * t + 1);
  }
}

module.exports = ExpandingBox;
